import { z } from "zod";
import type {
	PlaceKind,
	TransportAllocationState,
	TransportDisplayStatus,
	TransportFlightRef,
	TransportRequestSource,
	VehicleType,
} from "./transport";

/**
 * A participant's own transfers (`GET /me/transport`, active role
 * PARTICIPANT; user 2026-11-04). The trips the person rides as a named, live
 * passenger, read the way their NOC reads them (contracts' NOC trip view):
 * the status never says SHORT, and the vehicles / drivers are the ones the
 * NOC view already lists. Never another passenger's name, a passenger count,
 * seats, another organisation, or a trip id / reference.
 */

/** One end of a trip: what the person needs to find it, no ids. */
export interface ParticipantTripPlace {
	kind: PlaceKind;
	name: string;
	/** Venue / entry point code; null for a transport place (a hotel…). */
	code: string | null;
}

/** A vehicle on the trip, as the NOC view shows it: no seats, no passenger count. */
export interface ParticipantTripVehicle {
	state: TransportAllocationState;
	vehicle_type: VehicleType;
	plate: string;
	driver_name: string;
	driver_phone: string;
}

export interface ParticipantTrip {
	/**
	 * The trip's key for its detail page (`GET /me/transport/:key`): an
	 * opaque token made from the trip AND the login (md5 of both), never the
	 * trip id — two people on one trip get different keys, so comparing them
	 * says nothing. Only the login it was made for can open it.
	 */
	key: string;
	/** ARR (arrival pickup), DEP (to the departure), ADHOC (a venue trip). */
	source: TransportRequestSource;
	status: Exclude<TransportDisplayStatus, "SHORT">;
	/** ISO UTC; the GMS shows it in the viewer's zone. */
	pickup_at: string;
	/** An ADHOC trip's way back, when booked. */
	return_pickup_at: string | null;
	from: ParticipantTripPlace | null;
	to: ParticipantTripPlace | null;
	/** The person's OWN leg's flight (the transfer it follows), else null. */
	flight: TransportFlightRef | null;
	/** The person's own leg's service number when it has no flight row (a coach, a ferry, a typed flight). */
	service_no: string | null;
	vehicles: ParticipantTripVehicle[];
}

/**
 * Upcoming (still to come, or on the road — the transport list's Upcoming
 * tab rule) soonest first, then the recent past, latest first. Not paged: a
 * person rides one transfer per travel leg (`uq_trp_leg_live`: two) plus the
 * venue trips they are named on, so both lists are short; they are still
 * capped (`PARTICIPANT_TRANSPORT_UPCOMING_MAX`, `PARTICIPANT_TRANSPORT_PAST_MAX`).
 */
export interface ParticipantTransport {
	upcoming: ParticipantTrip[];
	past: ParticipantTrip[];
}

export const PARTICIPANT_TRANSPORT_UPCOMING_MAX = 50;
export const PARTICIPANT_TRANSPORT_PAST_MAX = 20;

/** `ParticipantTrip.key`: 32 lower-case hex characters. */
export const PARTICIPANT_TRIP_KEY_PATTERN = /^[0-9a-f]{32}$/;

/** `GET /me/transport/:key`. */
export const participantTripKeyParam = z.object({ key: z.string().regex(PARTICIPANT_TRIP_KEY_PATTERN) });

/** One end of a trip on its detail page: where to find it. */
export interface ParticipantTripDetailPlace extends ParticipantTripPlace {
	/**
	 * What to search a map for besides the name: a transport place's address
	 * (a hotel), a venue's city; null when nothing is stored. No coordinates
	 * are kept anywhere.
	 */
	address: string | null;
	/** An entry point's terminal ("T1"), else null. */
	terminal: string | null;
}

/** The person's transfer the other way (an arrival's departure, or back), when they ride one. */
export interface ParticipantLinkedTrip {
	key: string;
	source: TransportRequestSource;
	status: Exclude<TransportDisplayStatus, "SHORT">;
	pickup_at: string;
}

/**
 * `GET /me/transport/:key` — one of the caller's own trips (404 when they do
 * not ride it as a live passenger). The list item's fields, the ends with
 * their address, and the linked trip. Same privacy as the list: no other
 * rider, no count, no seats, no organisation, no id or reference.
 */
export interface ParticipantTripDetail extends Omit<ParticipantTrip, "from" | "to"> {
	from: ParticipantTripDetailPlace | null;
	to: ParticipantTripDetailPlace | null;
	/** ARR ↔ DEP: their transfer the other way; null for a venue trip (its way back is `return_pickup_at`) or none booked. */
	linked: ParticipantLinkedTrip | null;
}
